import React, { useMemo, useState } from "react";
import pluswhite from "assets/images/icon/plus-white.png";
import filtersearch from "assets/images/icon/filter-search.png";
import Input from "components/Input";
import Table from "components/Table";
import AddWorkPlacesModal from "pages/AddWorkplaceModal";
import WorkPlaceDetailsModal from "pages/WorkplaceDetailsModal";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getWorkplaces, deleteWorkplace } from "services/workplaces";

const headers = [
    { headerName: "Name", key: "name" },
    { headerName: "Address", key: "address" },
    { headerName: "Active Status", key: "active_status" },
    { headerName: "", key: "action" },
];

const ManageWorkplaces = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedWorkplace, setSelectedWorkplace] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [defaultEditing, setDefaultEditing] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const queryClient = useQueryClient();

    const { data: workplacesRaw = [], isLoading } = useQuery({ queryKey: ['workplaces'], queryFn: getWorkplaces });

    const deleteMutation = useMutation({
        mutationFn: (id) => deleteWorkplace(id),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['workplaces'] }); setDeleteTarget(null); },
    });

    const tableData = useMemo(() => workplacesRaw
        .filter(w => !searchTerm || w.name?.toLowerCase().includes(searchTerm.toLowerCase()))
        .map(w => ({
            id: w.id,
            name: w.name,
            address: [w.addressLine1, w.city, w.state, w.zip].filter(Boolean).join(', ') || '—',
            active_status: w.active ? 'Active' : 'Inactive',
        })),
        [workplacesRaw, searchTerm]);

    const handleEdit = (row) => {
        setSelectedWorkplace(workplacesRaw.find(w => w.id === row.id) || row);
        setDefaultEditing(true);
        setDetailsOpen(true);
    };

    const handleView = (row) => {
        setSelectedWorkplace(workplacesRaw.find(w => w.id === row.id) || row);
        setDefaultEditing(false);
        setDetailsOpen(true);
    };

    const handleDelete = (row) => setDeleteTarget(workplacesRaw.find(w => w.id === row.id) || row);

    return (
        <>
            <div>
                <div className='d-flex justify-content-between align-content-end border-bottom pb-4'>
                    <div>
                        <h2 className='sub-title'>Manage Workplaces</h2>
                        <button className='button-orange py-2 mt-3 rounded-pill d-flex justify-content-center align-items-center gap-2' onClick={() => setIsOpen(true)}>
                            <img src={pluswhite} alt="add" />Add Workplace
                        </button>
                    </div>
                    <div className='align-content-end'>
                        <Input placeholder='Search by Name' startIcon={filtersearch} style={{ width: '300px' }} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                </div>
                <div>
                    {isLoading ? (
                        <p className="text-center mt-4">Loading workplaces...</p>
                    ) : (
                        <Table
                            headers={headers}
                            data={tableData}
                            showPagination={true}
                            handleEdit={handleEdit}
                            handleView={handleView}
                            handleDelete={handleDelete}
                        />
                    )}
                </div>
            </div>
            <AddWorkPlacesModal
                open={isOpen}
                onClose={() => { setIsOpen(false); queryClient.invalidateQueries({ queryKey: ['workplaces'] }); }}
            />
            <WorkPlaceDetailsModal
                workplace={selectedWorkplace}
                open={detailsOpen}
                defaultEditing={defaultEditing}
                onClose={() => { setDetailsOpen(false); setSelectedWorkplace(null); }}
            />
            {deleteTarget && (
                <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
                        <div className="modal-content p-4">
                            <h5 className="mb-2">Delete Workplace</h5>
                            <p className="text-muted mb-4">Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This action cannot be undone.</p>
                            {deleteMutation.isError && <p className="text-danger mb-3">Failed to delete. Please try again.</p>}
                            <div className="d-flex gap-2 justify-content-end">
                                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>Cancel</button>
                                <button className="btn btn-danger rounded-pill px-4" onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}>
                                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ManageWorkplaces;
